import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { faClose } from "@fortawesome/pro-light-svg-icons";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";
import { SVG } from "~/components/ui/SVG";
import Button from "~/components/ui/Button";
import { SHOW_PAST_DUE_MODAL } from "~/helpers/customEvents";
import { paths } from "~/router/paths";
import { useAccount } from "~/hooks/useAccount";
import { PlanStatus } from "~/types/account";
import * as S from "./styles";

export const PastDueModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { account, getAccount } = useAccount();
  const [isShowing, setIsShowing] = useState(false);

  // Refresh account before opening — guards against stale local state
  // (e.g. user paid in another tab, Stripe retried the invoice in background).
  useEffect(() => {
    const handleShow = async () => {
      try {
        const fresh = await getAccount();
        if (fresh?.last_subscription?.status !== PlanStatus.PAST_DUE) return;
      } catch {
        // ignore refresh failures and fall through to show — fail-open is safer
      }
      setIsShowing(true);
    };
    window.addEventListener(SHOW_PAST_DUE_MODAL, handleShow);
    return () => window.removeEventListener(SHOW_PAST_DUE_MODAL, handleShow);
  }, [getAccount]);

  // Auto-close if the subscription is no longer past-due while the modal is open
  useEffect(() => {
    if (
      isShowing &&
      account?.last_subscription?.status !== PlanStatus.PAST_DUE
    ) {
      setIsShowing(false);
    }
  }, [isShowing, account?.last_subscription?.status]);

  const close = () => setIsShowing(false);

  if (!isShowing) return null;

  const handleUpdatePayment = () => {
    close();
    navigate(paths.settings.account);
  };

  const handleChangePlan = () => {
    close();
    navigate(paths.settings.plans);
  };

  return ReactDOM.createPortal(
    <S.ModalWrapper onClick={close}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.CloseContainer onClick={close}>
          <SVG icon={faClose} size="lg" />
        </S.CloseContainer>
        <S.HeaderRow>
          <S.IconCircle>
            <SVG icon={faExclamationCircle} size="xl" />
          </S.IconCircle>
          <S.Title>{t("past_due_modal.title")}</S.Title>
        </S.HeaderRow>
        <br></br>
        <S.Description>{t("past_due_modal.desc")}</S.Description>
        <S.ButtonRow>
          <Button outline onClick={handleChangePlan}>
            {t("past_due_modal.change_plan")}
          </Button>
          <Button
            color="white"
            bgColor="primary"
            radius={0.8}
            fontWeight={600}
            padding="13px 36px"
            onClick={handleUpdatePayment}>
            {t("past_due_modal.update_payment")}
          </Button>
        </S.ButtonRow>
      </S.ModalContent>
    </S.ModalWrapper>,
    document.body
  );
};
